import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../utils/adminApi';

const KINDS = [
  { key: 'info', label: 'Info', desc: 'A general notice', color: 'from-sky-500 to-blue-600', icon: 'fa-circle-info' },
  { key: 'sale', label: 'Sale', desc: 'A special offer or promotion', color: 'from-emerald-500 to-teal-600', icon: 'fa-tags' },
  { key: 'closed', label: 'Closed', desc: 'Shop is closed today', color: 'from-red-500 to-rose-600', icon: 'fa-lock' },
  { key: 'warning', label: 'Important', desc: 'Time-sensitive alert', color: 'from-amber-500 to-orange-600', icon: 'fa-triangle-exclamation' },
];

const AdminInbox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'announcement' ? 'announcement' : 'suggestions';
  const [tab, setTab] = useState(initialTab);

  // ANNOUNCEMENT state
  const [ann, setAnn] = useState({ is_active: false, kind: 'info', title: '', message: '' });
  const [annSaving, setAnnSaving] = useState(false);

  // SUGGESTIONS state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingS, setLoadingS] = useState(false);
  const [unread, setUnread] = useState(0);

  const loadAnn = useCallback(async () => {
    try {
      const r = await adminApi.get('/announcement');
      setAnn({
        is_active: !!r.data.is_active,
        kind: r.data.kind || 'info',
        title: r.data.title || '',
        message: r.data.message || '',
      });
    } catch { toast.error('Could not load announcement'); }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setLoadingS(true);
    try {
      const r = await adminApi.get('/suggestions');
      setSuggestions(r.data.suggestions || []);
      setUnread(r.data.unread || 0);
    } catch { toast.error('Could not load suggestions'); }
    finally { setLoadingS(false); }
  }, []);

  useEffect(() => { loadAnn(); loadSuggestions(); }, [loadAnn, loadSuggestions]);

  const saveAnn = async (payload) => {
    setAnnSaving(true);
    try {
      const r = await adminApi.put('/announcement', payload);
      setAnn({
        is_active: !!r.data.is_active,
        kind: r.data.kind,
        title: r.data.title,
        message: r.data.message,
      });
      toast.success(payload.is_active ? 'Announcement is now live' : 'Announcement turned off');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally { setAnnSaving(false); }
  };

  const toggleAnn = () => saveAnn({ ...ann, is_active: !ann.is_active });
  const publish = () => saveAnn({ ...ann, is_active: true });

  const markRead = async (id) => {
    try {
      await adminApi.patch(`/suggestions/${id}/read`);
      setSuggestions((prev) => prev.map((s) => s.id === id ? { ...s, is_read: true } : s));
      setUnread((u) => Math.max(0, u - 1));
    } catch { toast.error('Could not update'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this suggestion?')) return;
    try {
      await adminApi.delete(`/suggestions/${id}`);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Could not delete'); }
  };

  const current = KINDS.find((k) => k.key === ann.kind) || KINDS[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/staff')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all shadow-md border border-gray-200" data-testid="back-to-admin-btn">
              <i className="fas fa-arrow-left"></i>
              <span>Back to Admin</span>
            </button>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-emerald-600">Communication</p>
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">Suggestions &amp; Notice Banner</h1>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="inline-flex items-center bg-white rounded-2xl border border-slate-200 shadow-md p-1.5 mb-6">
          <button onClick={() => setTab('suggestions')} className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${tab === 'suggestions' ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-800'}`} data-testid="tab-suggestions">
            <i className="fas fa-lightbulb mr-2"></i>Suggestions
            {unread > 0 && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-white text-emerald-600">{unread}</span>}
          </button>
          <button onClick={() => setTab('announcement')} className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${tab === 'announcement' ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-800'}`} data-testid="tab-announcement">
            <i className="fas fa-bullhorn mr-2"></i>Notice Banner
            {ann.is_active && <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-white text-emerald-600" title="Live">•</span>}
          </button>
        </div>

        {tab === 'suggestions' && (
          <div data-testid="suggestions-list">
            {loadingS ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-12 text-center text-slate-400">
                <i className="fas fa-spinner fa-spin text-3xl mb-2"></i>
                <p>Loading suggestions...</p>
              </div>
            ) : suggestions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-12 text-center" data-testid="suggestions-empty">
                <i className="fas fa-inbox text-4xl text-slate-300 mb-3"></i>
                <p className="text-slate-600 font-semibold">No suggestions yet</p>
                <p className="text-xs text-slate-400 mt-1">Customers can leave suggestions from the Suggestions button in the home page header.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s) => {
                  const dt = s.created_at ? new Date(s.created_at) : new Date();
                  const timeStr = dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={s.id} className={`bg-white rounded-2xl shadow-md border p-4 transition-all ${s.is_read ? 'border-slate-200 opacity-70' : 'border-emerald-200 ring-2 ring-emerald-100'}`} data-testid={`suggestion-${s.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {!s.is_read && <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-white px-2 py-0.5 rounded-full">NEW</span>}
                            <p className="font-bold text-slate-900">{s.name || 'Anonymous'}</p>
                            {s.phone && <span className="text-xs text-slate-500"><i className="fas fa-phone mr-1"></i>{s.phone}</span>}
                            {s.email && <span className="text-xs text-slate-500"><i className="fas fa-envelope mr-1"></i>{s.email}</span>}
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{s.message}</p>
                          <p className="text-[10px] text-slate-400 mt-2 font-mono uppercase tracking-widest">{timeStr}</p>
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          {!s.is_read && (
                            <button onClick={() => markRead(s.id)} className="p-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-xs font-bold" title="Mark as read" data-testid={`mark-read-${s.id}`}>
                              <i className="fas fa-check"></i>
                            </button>
                          )}
                          <button onClick={() => remove(s.id)} className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold" title="Delete" data-testid={`delete-suggestion-${s.id}`}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'announcement' && (
          <div className="grid md:grid-cols-[1fr_1fr] gap-5" data-testid="announcement-form">
            {/* Editor */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Home Page Banner</p>
                  <h2 className="font-black text-slate-900">Set the message customers see</h2>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="announcement-toggle">
                  <input type="checkbox" checked={ann.is_active} onChange={toggleAnn} className="peer sr-only" />
                  <div className="relative w-11 h-6 bg-slate-200 peer-checked:bg-emerald-500 rounded-full transition-all">
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${ann.is_active ? 'translate-x-5' : ''}`}></div>
                  </div>
                  <span className={`text-xs font-bold ${ann.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>{ann.is_active ? 'Live' : 'Off'}</span>
                </label>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 block">Type</label>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {KINDS.map((k) => (
                    <button
                      key={k.key}
                      onClick={() => setAnn((a) => ({ ...a, kind: k.key }))}
                      className={`text-left p-3 rounded-xl border-2 transition-all ${ann.kind === k.key ? `border-transparent bg-gradient-to-br ${k.color} text-white shadow-lg` : 'border-slate-200 hover:border-slate-300 text-slate-700 bg-white'}`}
                      data-testid={`kind-${k.key}`}
                    >
                      <i className={`fas ${k.icon} mb-1`}></i>
                      <p className="font-bold text-sm leading-tight">{k.label}</p>
                      <p className={`text-[10px] leading-tight mt-0.5 ${ann.kind === k.key ? 'text-white/80' : 'text-slate-400'}`}>{k.desc}</p>
                    </button>
                  ))}
                </div>

                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Title <span className="text-slate-300 normal-case">(short)</span></label>
                <input
                  type="text"
                  value={ann.title}
                  onChange={(e) => setAnn((a) => ({ ...a, title: e.target.value }))}
                  placeholder="e.g. Summer Sale · Closed for Eid · New Service"
                  maxLength={120}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none mb-3"
                  data-testid="announcement-title"
                />

                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 block">Message</label>
                <textarea
                  rows={3}
                  value={ann.message}
                  onChange={(e) => setAnn((a) => ({ ...a, message: e.target.value }))}
                  placeholder="Full details customers will see on the home page…"
                  maxLength={400}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  data-testid="announcement-message"
                />
                <p className="text-[10px] text-slate-400 mt-1 text-right">{ann.message.length}/400</p>

                <button
                  onClick={publish}
                  disabled={annSaving}
                  className="w-full mt-3 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold rounded-xl disabled:opacity-60 transition-all shadow-lg"
                  data-testid="announcement-publish"
                >
                  {annSaving ? <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</> : <><i className="fas fa-satellite-dish mr-2"></i>{ann.is_active ? 'Update live banner' : 'Publish banner'}</>}
                </button>
              </div>
            </div>

            {/* Live preview */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2 px-1">Live preview</p>
              <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${current.color} text-white p-5 shadow-xl`} data-testid="announcement-preview">
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>
                <div className="flex items-start gap-3 relative">
                  <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <i className={`fas ${current.icon} text-white`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/80">{current.label}</p>
                    {ann.title ? <p className="font-bold text-lg leading-tight mt-0.5">{ann.title}</p> : <p className="italic text-white/60 mt-1">Add a title above…</p>}
                    {ann.message && <p className="text-sm text-white/90 mt-1 leading-snug">{ann.message}</p>}
                  </div>
                </div>
              </div>
              {!ann.is_active && (
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                  <i className="fas fa-info-circle mr-1"></i>
                  Preview only — flip the toggle to <strong>Live</strong> to make this appear on the home page.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInbox;
